/**
    * @description      : User Invoice page for renters to view their invoices
    * @author           : Saif
    * @group            : 
    * @created          : 04/11/2024 - 22:30:43
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 04/11/2024
    * - Author          : Saif
    * - Modification    : 
**/
import { useState, useEffect } from "react";
import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileInvoiceDollar,
  faDownload,
  faEye,
  faSearch,
  faCalendarDays,
  faBuilding,
  faCreditCard,
  faCreditCardAlt
} from "@fortawesome/free-solid-svg-icons";
import { useGet } from "../../Api";
import CustomTable from "../../Components/CustomTable";
import CustomButton from "../../Components/CustomButton";
import PaymentModal from "../../Components/PaymentModal";
import { useNotification } from '../../Components/GlobalNotification';

import "./style.css";

export const UserInvoice = () => {
  const { showNotification } = useNotification();
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [invoiceStats, setInvoiceStats] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  const { ApiData: InvoicesData, loading: InvoicesLoading, error: InvoicesError, get: GetInvoices } = useGet(`/invoice`);

  useEffect(() => {
    document.title = 'kvr User | My Invoices';
    GetInvoices();
  }, []);

  useEffect(() => {
   
    if (InvoicesData && InvoicesData?.success === true) {
      // API responses vary: sometimes `data` is an array, sometimes `{ data: [] }`
      const invoiceList = Array.isArray(InvoicesData?.data?.data)
        ? InvoicesData.data.data
        : Array.isArray(InvoicesData?.data)
          ? InvoicesData.data
          : [];
     
      setInvoices(invoiceList);
      setFilteredInvoices(invoiceList);
      
      // Calculate stats from invoice data
      const stats = calculateInvoiceStats(invoiceList);
      setInvoiceStats(stats);
      
    }
  }, [InvoicesData]);

  useEffect(() => {
    // Filter invoices based on search term and status
    let filtered = invoices;
    
    if (searchTerm) {
      filtered = filtered?.filter(invoice => 
        invoice?.invoice_id?.toString().includes(searchTerm?.toLowerCase()) ||
        invoice?.billing_for?.toLowerCase().includes(searchTerm?.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered?.filter(invoice => invoice?.status === statusFilter);
    }
    
    setFilteredInvoices(filtered);
  }, [invoices, searchTerm, statusFilter]);

  const calculateInvoiceStats = (invoiceList) => {
    const list = Array.isArray(invoiceList) ? invoiceList : [];

    const totalInvoices = list.length;
    const totalAmount = list.reduce((sum, invoice) => sum + parseFloat(invoice?.total || 0), 0);
    const paidAmount = list
      .filter((invoice) => invoice?.status === 'paid')
      .reduce((sum, invoice) => sum + parseFloat(invoice?.total || 0), 0);
    const pendingAmount = list
      .filter((invoice) => invoice?.status === 'pending')
      .reduce((sum, invoice) => sum + parseFloat(invoice?.total || 0), 0);

    return {
      totalInvoices,
      totalAmount,
      paidAmount,
      pendingAmount
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      'paid': 'badge-success',
      'pending': 'badge-warning',
      'overdue': 'badge-danger',
      'cancelled': 'badge-secondary'
    };
    
    return (
      <span className={`badge ${statusClasses[status] || 'badge-secondary'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const handleDownloadInvoice = (invoiceId) => {
    // Implement download functionality
    console.log('Downloading invoice:', invoiceId);
  };

  const handleViewInvoice = (invoice) => {
    // If invoice is paid and has details_url, open the URL
    if (invoice.status === 'paid' && invoice.details_url) {
      // Construct the full URL if it's a relative path
      const fullUrl = invoice.details_url.startsWith('http') 
        ? invoice.details_url 
        : `https://kvr.kayifi.com/${invoice.details_url}`;
      
      // Open in new tab
      window.open(fullUrl, '_blank');
    } else {
      // For unpaid invoices or invoices without details_url, show toast notification
      console.log('Viewing invoice:', invoice?.invoice_id);
      showNotification({ type: 'info', title: 'Information', message: 'Invoice details will be available after payment is completed.' });
    }
  };

  const handlePayInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowPaymentModal(true);
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedInvoice(null);
    // Refresh invoices after payment
    GetInvoices();
  };

  const tableHeaders = [
    { key: 'invoice_id', title: 'Invoice #' },
    { key: 'billing_for', title: 'Description' },
    { key: 'total', title: 'Amount' },
    { key: 'date', title: 'Date' },
    { key: 'status', title: 'Status' },
    // { key: 'payment_status', title: 'Payment Status' },
    { key: 'actions', title: 'Actions' }
  ];

  return (
    <>
      <DashboardLayout>
        <div className="invoice-container">
          <div className="row">
            <div className="col-12">
              <h2 className="page-title">
                <FontAwesomeIcon icon={faFileInvoiceDollar} className="me-2" />
                My Invoices
              </h2>
            </div>
          </div>

          {/* Invoice Stats Cards */}
          {invoiceStats && (
            <div className="row mb-4">
              <div className="col-md-3 mb-3">
                <div className="invoice-card total-card">
                  <div className="card-icon">
                    <FontAwesomeIcon icon={faFileInvoiceDollar} />
                  </div>
                  <div className="card-content">
                    <h3 className="card-title">Total Invoices</h3>
                    <p className="card-amount">
                      {invoiceStats.totalInvoices}
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-md-3 mb-3">
                <div className="invoice-card total-amount-card">
                  <div className="card-icon">
                    <FontAwesomeIcon icon={faCreditCard} />
                  </div>
                  <div className="card-content">
                    <h3 className="card-title">Total Amount</h3>
                    <p className="card-amount">
                      {formatCurrency(invoiceStats.totalAmount)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-md-3 mb-3">
                <div className="invoice-card paid-card">
                  <div className="card-icon">
                    <FontAwesomeIcon icon={faCalendarDays} />
                  </div>
                  <div className="card-content">
                    <h3 className="card-title">Paid Amount</h3>
                    <p className="card-amount">
                      {formatCurrency(invoiceStats.paidAmount)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-md-3 mb-3">
                <div className="invoice-card pending-card">
                  <div className="card-icon">
                    <FontAwesomeIcon icon={faBuilding} />
                  </div>
                  <div className="card-content">
                    <h3 className="card-title">Pending Amount</h3>
                    <p className="card-amount">
                      {formatCurrency(invoiceStats.pendingAmount)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filters and Search */}
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="search-box">
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                <input
                  type="text"
                  className="form-control search-input"
                  placeholder="Search invoices..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="overdue">Overdue</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="col-md-3">
              <CustomButton
                text="Export"
                variant="primary"
                className="w-100"
                onClick={() => console.log('Export invoices')}
              />
            </div>
          </div>

          {/* Invoices Table */}
          <div className="row">
            <div className="col-12">
              {InvoicesLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : InvoicesError ? (
                <div className="alert alert-danger" role="alert">
                  Failed to load invoices. Please try again.
                </div>
              ) : (
                <div className="invoice-table-container">
                  <CustomTable headers={tableHeaders}>
                    <tbody>
                      {filteredInvoices?.length > 0 ? (
                        filteredInvoices?.map((invoice, index) => (
                          <tr key={index} className="invoice-row">
                            <td>
                              <strong>{invoice.invoice_id}</strong>
                            </td>
                            <td>{invoice.billing_for}</td>
                            <td>
                              <strong>{formatCurrency(invoice.total)}</strong>
                            </td>
                            <td>{formatDate(invoice.date)}</td>
                            <td>{getStatusBadge(invoice.status)}</td>
                            {/* <td>
                              <span className={`payment-status ${invoice.payment_status_code === 0 ? 'text-warning' : 'text-success'}`}>
                                {invoice.payment_status}
                              </span>
                            </td> */}
                            <td>
                              <div className="action-buttons">
                                <CustomButton
                                  text={invoice.status === 'paid' ? 'View Details' : 'View'}
                                  variant="outline-primary"
                                  size="sm"
                                  icon={faEye}
                                  onClick={() => handleViewInvoice(invoice)}
                                  className="me-2"
                                />
                                <CustomButton
                                  text="Download"
                                  variant="outline-success"
                                  size="sm"
                                  icon={faDownload}
                                  onClick={() => handleDownloadInvoice(invoice.invoice_id)}
                                  className="me-2"
                                />
                                {invoice.status !== 'paid' && (
                                  <CustomButton
                                    text="Pay Now"
                                    variant="primary"
                                    size="sm"
                                    icon={faCreditCardAlt}
                                    onClick={() => handlePayInvoice(invoice)}
                                  />
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            <p className="text-muted">No invoices found</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </CustomTable>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Payment Modal */}
        <PaymentModal
          show={showPaymentModal}
          close={handleClosePaymentModal}
          invoice={selectedInvoice}
        />
      </DashboardLayout>
    </>
  );
};
